import { useStrings } from '../state/AppContext';

export interface HintBannerProps {
  answer: string;
  /** Answer positions revealed by hints (0-based). */
  revealed: readonly number[];
}

/** Ghost tiles for hint-revealed letters. Never consumes a guess row (spec §3.6). */
export function HintBanner({ answer, revealed }: HintBannerProps) {
  const t = useStrings();
  if (revealed.length === 0) return null;
  const shown = new Set(revealed);

  return (
    <div
      role="group"
      aria-label={t.hint.groupLabel(revealed.length, answer.length)}
      className="glass flex items-center gap-3 rounded-2xl px-4 py-2"
    >
      <span className="text-[11px] font-semibold tracking-[0.18em] text-dim">{t.hint.label}</span>
      <div className="flex gap-1.5">
        {Array.from({ length: answer.length }, (_, i) => {
          const letter = shown.has(i) ? answer[i]!.toUpperCase() : '';
          // Placeholder (not-yet-revealed) tiles are purely decorative — the
          // group's label already states how many of them are revealed, so
          // giving each empty tile its own `role="img"` announcement (spec
          // review: "position 2: hidden" repeated up to 5 times) was noise.
          // A revealed tile keeps its own accessible name since that letter
          // is the actual information being conveyed.
          if (!letter) {
            return (
              <span
                key={i}
                aria-hidden="true"
                className="grid h-7 w-7 place-items-center rounded-md border text-sm font-bold border-tile-border text-dim"
              >
                ·
              </span>
            );
          }
          return (
            <span
              key={i}
              role="img"
              aria-label={t.hint.position(i + 1, letter)}
              className="grid h-7 w-7 place-items-center rounded-md border text-sm font-bold border-present text-present"
            >
              <span aria-hidden="true">{letter}</span>
            </span>
          );
        })}
      </div>
      <span className="ml-auto text-[11px] text-dim">{t.hint.perHint}</span>
    </div>
  );
}
